<script setup lang="ts">
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import ProjectData = App.Data.Project.ProjectData
import DistributionBar from '@/components/DistributionBar.vue'
import DocumentTable from '@/components/DocumentTable.vue'
import EnergyCertificateScale from '@/components/EnergyCertificateScale.vue'
import FinestCard from '@/components/FinestCard.vue'
import RentalUnitsTable from '@/components/RentalUnitsTable.vue'
import ValueDisplay from '@/components/ValueDisplay.vue'
import { filterDocuments } from '@/pages/banks/financing-request/utils'
import { asEuro } from '@/utils/amount-unit'
import Galleria from 'primevue/galleria'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AttachmentData = App.Data.AttachmentData

const props = defineProps<{
  estateObject: EstateObjectData
  project: ProjectData
  estateObjectCount: number
  attachments: AttachmentData[]
}>()

const { n, t } = useI18n()

const additionalSecurities = computed(() => {
  const securities = (props.project.collaterals ?? [])
    .map((collateral) =>
      [
        collateral.collateral_type ? t(`enums.collateralType.${collateral.collateral_type}`) : null,
        collateral.nominal_value ? n(Number(collateral.nominal_value), 'euros') : null,
      ]
        .filter(Boolean)
        .join(' '),
    )
    .filter(Boolean)

  return securities.length > 0 ? securities.join(', ') : null
})

const objectInformationItems = computed(() => [
  {
    label: t('labels.address'),
    value: props.estateObject.address,
    icon: 'pi pi-map-marker',
    sensitiveField: 'EstateObjectData.address',
  },
  {
    label: t('labels.city'),
    value: props.estateObject.city,
  },
  {
    label: t('labels.estate_object_type'),
    value: t(`enums.estateObjectType.${props.estateObject.estate_object_type}`),
    icon: 'pi pi-home',
  },
  {
    label: t('labels.business_type'),
    value: t(`enums.businessType.${props.estateObject.business_type}`),
  },
  {
    label: t('labels.construction_year'),
    value: props.estateObject.construction_year,
  },
  {
    label: t('labels.additional_securities'),
    value: additionalSecurities.value,
  },
])

const financingKpis = computed(() => [
  {
    label:
      props.estateObjectCount > 1 ? t('labels.total_financing_amount_multiple') : t('labels.total_financing_amount'),
    value: Number(props.project.total_financing_amount),
    strong: true,
  },
  {
    label: t('labels.purchase_price'),
    value: props.estateObject.purchase_price,
  },
  {
    label: t('labels.equity'),
    value: Number(props.estateObject.equity),
    highlight: true,
  },
  {
    label: t('labels.modernization_amount'),
    value: props.estateObject.modernization_amount,
  },
  {
    label: t('labels.additional_purchase_costs'),
    value: props.estateObject.additional_purchase_costs,
  },
  {
    label: t('labels.broker_commission'),
    value: asEuro(
      props.estateObject.broker_commission ?? null,
      props.estateObject.broker_commission_unit ?? 'percent',
      Number(props.estateObject.purchase_price) || null,
    ),
  },
])

//TODO: Bilder pro project oder pro estate object?
const galleryImages = computed(() => {
  const imgs = filterDocuments([], ['photo', 'photo.project_preview'])
    .filter((att) => att.path)
    .map((att) => ({
      itemImageSrc: att.path,
      thumbnailImageSrc: att.path,
      alt: att.description || att.filename || 'image',
    }))

  if (imgs.length === 0) {
    return [
      {
        itemImageSrc: null,
        thumbnailImageSrc: null,
        alt: 'placeholder',
      },
    ]
  }

  return imgs
})

const galleryResponsiveOptions = ref([
  {
    breakpoint: '1300px',
    numVisible: 4,
  },
  {
    breakpoint: '575px',
    numVisible: 1,
  },
])

const kpis = computed(() => [
  {
    label: t('labels.purchase_price_per_living_square_meter'),
    value: props.estateObject.actual_rent_living_square_meter,
  },
  {
    label: t('labels.purchase_price_per_business_square_meter'),
    value: props.estateObject.actual_rent_business_square_meter,
  },
])
</script>

<template>
  <div>
    <div class="mt-2 flex flex-col gap-6 lg:flex-row">
      <div class="flex flex-col gap-6 lg:basis-2/3">
        <FinestCard :title="t('object_information')" icon="pi pi-list">
          <div class="mt-2 grid gap-x-12 gap-y-8 md:grid-cols-2 xl:grid-cols-3">
            <ValueDisplay
              v-for="item in objectInformationItems"
              :key="item.label"
              :label="item.label"
              :value="item.value"
              :sensitive-field="item.sensitiveField"
              format="string"
              css="flex items-center gap-3 font-semibold text-surface-950">
              <i v-if="item.icon" :class="item.icon" class="text-surface-500 text-xl!" aria-hidden="true" />
            </ValueDisplay>
          </div>
        </FinestCard>
        <FinestCard :title="t('object_images')" icon="pi pi-image">
          <Galleria
            :value="galleryImages"
            :responsive-options="galleryResponsiveOptions"
            :num-visible="5"
            container-style="max-width: 100%">
            <template #item="slotProps">
              <img
                v-if="slotProps.item.itemImageSrc"
                :src="slotProps.item.itemImageSrc"
                :alt="slotProps.item.alt"
                style="width: 100%" />
              <div
                v-else
                class="bg-surface-200 text-surface-500 flex items-center justify-center"
                style="width: 100%; height: 360px">
                {{ t('no_images_available') }}
              </div>
            </template>
            <template #thumbnail="slotProps">
              <img
                v-if="slotProps.item.thumbnailImageSrc"
                :src="slotProps.item.thumbnailImageSrc"
                :alt="slotProps.item.alt" />
            </template>
          </Galleria>
        </FinestCard>
        <!--FinestCard :title="t('why')" icon="pi pi-question">
          <p class="text-surface-400 mb-4">{{ t('investment_story_of_investor') }}</p>
          <p>
            {{ t('coming_soon') }}
          </p>
        </FinestCard-->
        <FinestCard :title="t('rental_overview')" icon="pi pi-list">
          <p class="text-surface-400 mb-4">{{ t('overview_of_all_units') }}</p>
          <RentalUnitsTable :rental-units="estateObject.rental_units ?? []" />
        </FinestCard>
        <FinestCard :title="t('documents')" icon="pi pi-file">
          <DocumentTable :documents="attachments" read-only />
        </FinestCard>
      </div>

      <div class="flex flex-col gap-6 lg:basis-1/3">
        <FinestCard :title="t('financing')" icon="pi pi-chart-line">
          <dl class="mt-2 flex flex-col">
            <div
              v-for="item in financingKpis"
              :key="item.label"
              class="border-surface-200 flex items-baseline justify-between gap-6 border-b py-5 last:border-b-0 last:pb-0">
              <dt class="text-surface-500">
                {{ item.label }}
              </dt>
              <dd
                class="text-surface-950 text-right text-xl font-semibold"
                :class="{
                  'text-2xl! font-bold': item.strong,
                  'text-success-800!': item.highlight,
                }">
                {{ n(item.value, 'euros') }}
              </dd>
            </div>
          </dl>
        </FinestCard>
        <FinestCard :title="t('object_details')" icon="pi pi-search">
          <dl class="mt-2 flex flex-col">
            <div
              class="border-surface-200 flex items-baseline justify-between gap-6 border-b py-5 last:border-b-0 last:pb-0">
              <dt class="text-surface-500">{{ t('labels.property_size') }}</dt>
              <dd class="text-surface-950 text-right text-xl font-semibold">
                {{ props.estateObject.property_size }} m²
              </dd>
            </div>
            <DistributionBar
              class="mt-6"
              :left-label="t('living_area')"
              :left-value="estateObject.living_space_size"
              :left-formatted-value="`${estateObject.living_space_size} m²`"
              :right-label="t('business_area')"
              :right-value="estateObject.business_space_size"
              :right-formatted-value="`${estateObject.business_space_size} m²`" />
          </dl>
        </FinestCard>
        <FinestCard :title="t('rental_structur')" icon="pi pi-chart-bar">
          <dl class="mt-2 flex flex-col">
            <div
              class="border-surface-200 flex items-baseline justify-between gap-6 border-b py-5 last:border-b-0 last:pb-0">
              <dt class="text-surface-500">{{ t('labels.actual_rent') }}</dt>
              <dd class="text-surface-950 text-right text-xl font-semibold">
                {{ n(props.estateObject.actual_rent, 'euros') }}
              </dd>
            </div>
            <DistributionBar
              class="mt-6"
              :left-label="t('living_rent')"
              :left-value="estateObject.actual_rent_living"
              :left-formatted-value="n(estateObject.actual_rent_living, 'euros')"
              :right-label="t('business_rent')"
              :right-value="estateObject.actual_rent_business"
              :right-formatted-value="n(estateObject.actual_rent_business, 'euros')" />
          </dl>
        </FinestCard>
        <FinestCard :title="t('kpis')" icon="pi pi-calculator">
          <dl class="mt-2 flex flex-col">
            <div
              v-for="item in kpis"
              :key="item.label"
              class="border-surface-200 flex items-baseline justify-between gap-6 border-b py-5 last:border-b-0 last:pb-0">
              <dt class="text-surface-500">
                {{ item.label }}
              </dt>
              <dd class="text-surface-950 text-right text-xl font-semibold">
                {{ n(item.value, 'euros') }}
              </dd>
            </div>
          </dl>
        </FinestCard>
        <FinestCard :title="t('energy_class')" icon="pi pi-bolt">
          <EnergyCertificateScale :value="estateObject.energy_certificate_type" />
        </FinestCard>
      </div>
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "object_information": "Objektinformationen",
    "financing": "Finanzierung",
    "kpis": "KPIs",
    "object_details": "Objektdetails",
    "object_images": "Objektbilder",
    "no_images_available": "No images available",
    "why": "Warum sollte die Bank dieses Projekt finanzieren?",
    "investment_story_of_investor": "Investment Story des Investors",
    "rental_structur": "Mietstruktur",
    "energy_class": "Energieklasse",
    "living_rent": "Wohnmiete",
    "business_rent": "Gewerbemiete",
    "living_area": "Wohnfläche",
    "business_area": "Gewerbefläche",
    "rental_overview": "Mietübersicht",
    "documents": "Dokumente",
    "overview_of_all_units": "Alle Einheiten im Überblick",
    "coming_soon": "Demnächst verfügbar...",
    "labels": {
      "address": "Adresse",
      "city": "Stadt",
      "estate_object_type": "Immobilientyp",
      "business_type": "Geschäftsart",
      "construction_year": "Baujahr",
      "additional_securities": "Zusätzliche Sicherheiten",
      "total_financing_amount": "Gesamtfinanzierungsbedarf",
      "total_financing_amount_multiple": "Gesamtfinanzierungsbedarf (alle Objekte)",
      "purchase_price": "Kaufpreis",
      "equity": "Eigenkapital",
      "modernization_amount": "Modernisierungskosten",
      "additional_purchase_costs": "Kaufnebenkosten",
      "broker_commission": "Maklerkosten",
      "property_size": "Grundstücksgröße",
      "actual_rent": "Aktuelle Mieteinnahmen",
      "purchase_price_per_living_square_meter": "Kaufpreis pro m² (Wohnfläche)",
      "purchase_price_per_business_square_meter": "Kaufpreis pro m² (Gewerbefläche)"
    }
  },
  "en": {
    "object_information": "Object information",
    "financing": "Financing",
    "kpis": "KPIs",
    "object_details": "Object details",
    "object_images": "Object images",
    "no_images_available": "Keine Bilder verfügbar",
    "why": "Why should the bank finance this project?",
    "investment_story_of_investor": "Investment story of the investor",
    "rental_structur": "Rental structure",
    "energy_class": "Energy class",
    "living_rent": "Residential rent",
    "business_rent": "Commercial rent",
    "living_area": "Residential area",
    "business_area": "Commercial area",
    "rental_overview": "Rental overview",
    "documents": "Documents",
    "overview_of_all_units": "Overview of all units",
    "coming_soon": "Coming soon...",
    "labels": {
      "address": "Address",
      "city": "City",
      "estate_object_type": "Property type",
      "business_type": "Business type",
      "construction_year": "Year built",
      "additional_securities": "Additional Securities",
      "total_financing_amount": "Total Financing Need",
      "total_financing_amount_multiple": "Total Financing Need (all objects)",
      "purchase_price": "Purchase price",
      "equity": "Equity",
      "modernization_amount": "Modernization costs",
      "additional_purchase_costs": "Additional purchase costs",
      "broker_commission": "Broker commission",
      "property_size": "Plot size",
      "actual_rent": "Current rental income",
      "purchase_price_per_living_square_meter": "Purchase price per m² (residential area)",
      "purchase_price_per_business_square_meter": "Purchase price per m² (commercial area)"
    }
  }
}
</i18n>
